import Icon from '$store/components/ui/Icon.tsx'
import CartButtonVTEX from '$store/islands/Header/Cart/vtex.tsx'
import { useSignal } from '@preact/signals'
import { ImageWidget } from 'apps/admin/widgets.ts'
import Image from 'apps/website/components/Image.tsx'
import { Alert as AlertProps } from 'site/components/header/Alert.tsx'
import MenuButton from 'site/components/header/Buttons/Menu.tsx'
import Button from 'site/components/ui/Button.tsx'
import Collapsable from 'site/components/ui/Collapsable.tsx'
import Login from './Login.tsx'
import type { CustomLink, INavItem } from './NavItem.tsx'

export interface Props {
    customLinks: CustomLink[]
    navItems: INavItem[]
    leftLink?: {
        label: string
        url: string
    }
    rightLink?: {
        label: string
        url: string
    }
    logo: ImageWidget
    alerts: AlertProps[]
    freeShippingValue: number
    /** @ignore Prop forwarded from Header to control minicart layout */
    useNewMinicartLayout?: boolean
    /** @ignore Prop forwarded from Header to disable Trinio on checkout click */
    disableTrinioCheckout?: boolean

    /**
     * @ignore
     */

    showMenu?: boolean
}

interface DrawerProps {
    item: INavItem | null
    logo: ImageWidget
    onClose: () => void
    open: boolean
}

function Drawer({ item, logo, onClose, open }: DrawerProps) {
    return (
        <div class='drawer'>
            <input
                id='my-drawer2'
                type='checkbox'
                class='drawer-toggle'
                checked={!!item}
            />
            <div
                data-open={open ? '' : undefined}
                class={`drawer-side transition-all duration-300 bg-white -left-full data-[open]:left-auto`}
            >
                <label
                    htmlFor='my-drawer2'
                    aria-label='close sidebar'
                    class='drawer-overlay z-[-1]'
                />
                <ul class='w-full min-h-full bg-white'>
                    <li>
                        <div class='flex flex-row justify-between items-center pl-5 pr-9 py-[18px] relative'>
                            <Button aria-label='Abrir menu de categorias' onClick={onClose}>
                                <Icon id='XMark' height={23} width={23} strokeWidth={0.01} />
                            </Button>
                            <Icon id='XMark' height={23} width={23} strokeWidth={0.01} />
                            {logo && (
                                <a
                                    href='/'
                                    aria-label='Store logo'
                                    class='absolute left-1/2 transform -translate-x-1/2'
                                >
                                    <Image
                                        src={logo}
                                        width={120}
                                        height={56}
                                        alt='Logo lojas torra'
                                    />
                                </a>
                            )}

                            <div class='flex items-center gap-4'>
                                <a class='flex px-0 font-normal' href='/wishlist'>
                                    <span class='sr-only'>ver produtos favoritos</span>
                                    <Icon
                                        id='Wishlist'
                                        class=''
                                        width={25}
                                        height={23}
                                        strokeWidth={0}
                                        fill='none'
                                    />
                                </a>
                                <CartButtonVTEX size='lg' />
                            </div>
                        </div>
                    </li>
                    <li class='text-sm font-lato'>
                        <div class='flex flex-col'>
                            {item?.url
                                ? (
                                    <a
                                        href={item.url}
                                        class='w-full px-5 py-4 text-lg font-bold text-neutral-0 bg-white border-b border-neutral-5'
                                    >
                                        {item?.label}
                                    </a>
                                )
                                : (
                                    <span class='w-full px-5 py-4 text-lg font-bold text-neutral-0 bg-white border-b border-neutral-5'>
                                        {item?.label}
                                    </span>
                                )}

                            {!!item?.children &&
                                item?.children.map((navSection) => {
                                    if (navSection.item && !navSection.label) {
                                        return navSection.item.map((navItem) => (
                                            <a
                                                href={navItem.url}
                                                class='w-full px-5 py-4 bg-white border-b border-neutral-5'
                                            >
                                                {navItem.label}
                                            </a>
                                        ))
                                    }
                                    return (
                                        <Collapsable
                                            class='w-full text-neutral-1 bg-white font-lato '
                                            title={
                                                <div class='flex items-center pl-5 pr-6 py-4 font-bold justify-between border-b border-neutral-5'>
                                                    {navSection.item && navSection.item.length > 0
                                                        ? (
                                                            <>
                                                                <span>{navSection.label}</span>
                                                                <Icon
                                                                    id='Arrow'
                                                                    width={17}
                                                                    height={8}
                                                                    class='rotate-0 group-open:rotate-180 transition-all ease-in-out duration-[400ms]'
                                                                />
                                                            </>
                                                        )
                                                        : (
                                                            <>
                                                                <a
                                                                    href={navSection.categoryUrl}
                                                                    class='w-ful'
                                                                >
                                                                    {navSection.label}
                                                                </a>
                                                            </>
                                                        )}
                                                </div>
                                            }
                                        >
                                            <div class='flex flex-col mt-5'>
                                                {!!navSection.item &&
                                                    navSection.item.map((navItem) => (
                                                        <a
                                                            href={navItem.url}
                                                            class='menu-item'
                                                        >
                                                            {navItem.label}
                                                        </a>
                                                    ))}
                                            </div>
                                        </Collapsable>
                                    )
                                })}
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    )
}

function Menu({
    customLinks,
    navItems,
    leftLink,
    rightLink,
    logo,
    alerts,
    showMenu,
}: Props) {
    const selectedItem = useSignal<number | null>(null)

    const onCloseCategoryDrawer = () => {
        selectedItem.value = null
    }

    const filteredAlerts = alerts.filter(
        (alert) => alert.text !== 'Cartão Torra',
    )

    return (
        <div
            class={`flex flex-col min-h-full w-full bg-white h-[100dvh] overflow-auto`}
        >
            <div class='flex flex-row justify-between items-center pl-5 pr-9 py-[18px] relative'>
                <MenuButton type='close' />
                {logo && (
                    <a
                        href='/'
                        aria-label='Store logo'
                        class='absolute left-1/2 transform -translate-x-1/2'
                    >
                        <Image src={logo} width={120} height={56} alt='Logo lojas torra' />
                    </a>
                )}

                <div class='flex items-center gap-4'>
                    <a class='flex px-0 font-normal' href='/wishlist'>
                        <span class='sr-only'>ver produtos favoritos</span>
                        <Icon
                            id='Wishlist'
                            class='text-neutral-1'
                            width={25}
                            height={23}
                            strokeWidth={0}
                            fill='none'
                        />
                    </a>
                    <CartButtonVTEX size='lg' />
                </div>
            </div>

            <div class='divide-y divide-white'>
                <div class='w-full bg-primary-700 py-[15px] px-[17px] flex items-center gap-2'>
                    <Login isWhite />
                </div>

                {customLinks.map((item) => (
                    <a
                        href={item.href}
                        class='w-full bg-primary-700 py-[15px] px-[17px] flex items-center gap-2'
                    >
                        <Icon
                            id={item.icon}
                            width={item.width}
                            height={item.heigth}
                            class='text-white'
                        />
                        <span class='text-white text-sm font-bold font-lato'>
                            {item.label}
                        </span>
                    </a>
                ))}
            </div>

            <div>
                {/* Novidades */}
                {leftLink && (
                    <a
                        href={leftLink.url}
                        class='flex w-full justify-between items-center pl-5 pr-6 py-4 border-b text-sm border-neutral-5 font-bold'
                    >
                        {leftLink.label}
                    </a>
                )}

                {/* Itens */}
                {navItems.map((item, index) => {
                    const childrenLength = item.children?.length ?? 0

                    return (
                        <>
                            <a
                                // type='button'
                                href={item.url}
                                class='flex w-full justify-between items-center pl-5 pr-6 py-4 border-b border-neutral-5'
                                onClick={(event) => {
                                    event.preventDefault()
                                    if (childrenLength === 0) {
                                        globalThis.location.href = item.url
                                        return
                                    }

                                    selectedItem.value = index

                                    setTimeout(() => {
                                        const drawer = document.getElementById(
                                            'mobile-menu',
                                        ) as HTMLDivElement

                                        if (!drawer) throw new Error('Drawer not found')

                                        drawer.scrollTo({ top: 0, behavior: 'smooth' })
                                    })
                                }}
                            >
                                <span class='text-neutral-0 font-lato text-sm'>
                                    {item.label}
                                </span>
                                {childrenLength > 0 && (
                                    <>
                                        <Icon
                                            id='ArrowRight'
                                            width={8}
                                            height={17}
                                            class='text-primary-700'
                                        />
                                    </>
                                )}
                            </a>
                            <Drawer
                                item={item}
                                logo={logo}
                                open={selectedItem.value === index}
                                onClose={onCloseCategoryDrawer}
                            />
                        </>
                    )
                })}

                {/* Outlet */}
                {rightLink && (
                    <a
                        href={rightLink.url}
                        class='flex w-full justify-between items-center pl-5 pr-6 py-4 text-sm text-primary-700 border-b border-neutral-5 font-bold'
                    >
                        {rightLink.label}
                    </a>
                )}
            </div>

            <div class='divide-y divide-white'>
                {filteredAlerts.map((item) => (
                    <a
                        href={item.url}
                        class='w-full bg-neutral-2 font-lato py-[15px] px-[17px] flex items-center gap-2'
                    >
                        {item.icon && (
                            <Icon
                                id={item.icon}
                                height={item.height}
                                width={item.width}
                                class='text-white'
                            />
                        )}
                        <span class='text-white text-sm font-lato font-bold'>
                            {item.text}
                        </span>
                    </a>
                ))}
            </div>
        </div>
    )
}

export default Menu
